<template>
  <div>
    <b-card>
      <b-card-title>تغيير كلمة المرور</b-card-title>
      <b-card-text class="text-muted">
        تأكد من أن حسابك يستخدم كلمة مرور طويلة وعشوائية للحفاظ على الأمان.
      </b-card-text>

      <b-alert
        variant="warning"
        show
        class="mb-2"
      >
        <div class="alert-body">
          <feather-icon
            icon="AlertTriangleIcon"
            class="mr-50"
          />
          <span>بعد تغيير كلمة المرور، سيتم تسجيل خروجك وستحتاج إلى تسجيل الدخول مرة أخرى بكلمة المرور الجديدة.</span>
        </div>
      </b-alert>

      <validation-observer ref="passwordForm">
        <b-form @submit.prevent="handleChangePassword">
          <b-row>
            <!-- Current Password -->
            <b-col cols="12">
              <b-form-group
                label="كلمة المرور الحالية"
                label-for="current_password"
              >
                <validation-provider
                  #default="{ errors }"
                  name="كلمة المرور الحالية"
                  rules="required"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-form-input
                      id="current_password"
                      v-model="form.current_password"
                      :type="currentPasswordFieldType"
                      :state="errors.length > 0 ? false : null"
                      placeholder="أدخل كلمة المرور الحالية"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        :icon="currentPasswordToggleIcon"
                        class="cursor-pointer"
                        @click="toggleCurrentPasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- New Password -->
            <b-col cols="12">
              <b-form-group
                label="كلمة المرور الجديدة"
                label-for="password"
              >
                <validation-provider
                  #default="{ errors }"
                  name="كلمة المرور الجديدة"
                  vid="password"
                  rules="required|min:8"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-form-input
                      id="password"
                      v-model="form.password"
                      :type="newPasswordFieldType"
                      :state="errors.length > 0 ? false : null"
                      placeholder="أدخل كلمة المرور الجديدة (8 أحرف على الأقل)"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        :icon="newPasswordToggleIcon"
                        class="cursor-pointer"
                        @click="toggleNewPasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Confirm New Password -->
            <b-col cols="12">
              <b-form-group
                label="تأكيد كلمة المرور الجديدة"
                label-for="password_confirmation"
              >
                <validation-provider
                  #default="{ errors }"
                  name="تأكيد كلمة المرور"
                  rules="required|confirmed:password"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-form-input
                      id="password_confirmation"
                      v-model="form.password_confirmation"
                      :type="confirmPasswordFieldType"
                      :state="errors.length > 0 ? false : null"
                      placeholder="أكد كلمة المرور الجديدة"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        :icon="confirmPasswordToggleIcon"
                        class="cursor-pointer"
                        @click="toggleConfirmPasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Submit Button -->
            <b-col cols="12">
              <b-button
                type="submit"
                variant="primary"
                :disabled="isChangingPassword"
              >
                <b-spinner
                  v-if="isChangingPassword"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="LockIcon"
                  size="14"
                  class="mr-50"
                />
                تغيير كلمة المرور
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BCardText,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BRow,
  BCol,
  BSpinner,
  BAlert,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { mapGetters, mapActions } from 'vuex'
import router from '@/router'

export default {
  name: 'ChangePassword',
  components: {
    BCard,
    BCardTitle,
    BCardText,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BRow,
    BCol,
    BSpinner,
    BAlert,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      form: {
        current_password: '',
        password: '',
        password_confirmation: '',
      },
      currentPasswordFieldType: 'password',
      newPasswordFieldType: 'password',
      confirmPasswordFieldType: 'password',
      required,
    }
  },
  computed: {
    ...mapGetters('profile', {
      isChangingPassword: 'isChangingPassword',
      passwordError: 'passwordError',
    }),

    currentPasswordToggleIcon() {
      return this.currentPasswordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },

    newPasswordToggleIcon() {
      return this.newPasswordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },

    confirmPasswordToggleIcon() {
      return this.confirmPasswordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  methods: {
    ...mapActions('profile', {
      changePassword: 'changePassword',
    }),

    toggleCurrentPasswordVisibility() {
      this.currentPasswordFieldType = this.currentPasswordFieldType === 'password' ? 'text' : 'password'
    },

    toggleNewPasswordVisibility() {
      this.newPasswordFieldType = this.newPasswordFieldType === 'password' ? 'text' : 'password'
    },

    toggleConfirmPasswordVisibility() {
      this.confirmPasswordFieldType = this.confirmPasswordFieldType === 'password' ? 'text' : 'password'
    },

    async handleChangePassword() {
      const isValid = await this.$refs.passwordForm.validate()
      if (!isValid) {
        return
      }

      try {
        const response = await this.changePassword(this.form)

        this.$toast({
          component: this.$root.$refs.notificationToast,
          props: {
            title: 'نجاح',
            icon: 'CheckIcon',
            variant: 'success',
            text: response.message || 'تم تغيير كلمة المرور بنجاح. يرجى تسجيل الدخول مرة أخرى.',
          },
        })

        // Wait for 2 seconds before logout
        setTimeout(() => {
          // Clear auth data
          localStorage.removeItem('accessToken')
          localStorage.removeItem('userData')

          // Redirect to login
          router.push({ name: 'auth-login' })
        }, 2000)
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'فشل تغيير كلمة المرور'
        const errors = error.response?.data?.errors || {}

        let errorText = errorMessage
        if (Object.keys(errors).length > 0) {
          errorText += '<br><br>'
          Object.keys(errors).forEach(key => {
            errorText += `• ${errors[key].join('<br>')}<br>`
          })
        }

        this.$swal({
          icon: 'error',
          title: 'خطأ',
          html: errorText,
          confirmButtonText: 'حسناً',
          customClass: {
            confirmButton: 'btn btn-danger',
          },
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
